/**
 * SharedTripTimeline — collaborative activity stream for a single joint trip.
 *
 * Opens as a Dialog (drawer-style). Reads/posts to
 *   GET  /api/partner/shared-trips/{item_id}/timeline
 *   POST /api/partner/shared-trips/{item_id}/timeline
 *   DELETE /api/partner/shared-trips/timeline/{event_id}
 *
 * Only users who are active participants on the shared trip can post; the
 * backend enforces this (403 otherwise) — we surface that cleanly.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Heart, Send, Trash2, Loader2, Sparkles, Flag, Pin } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const EVENT_ICON = {
  note: Heart,
  milestone: Flag,
  reaction: Sparkles,
  photo_ref: Pin,
};
const EVENT_TINT = {
  note: "text-pink-500",
  milestone: "text-amber-500",
  reaction: "text-primary",
  photo_ref: "text-emerald-500",
};

export default function SharedTripTimeline({ open, onClose, trip, currentUserId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState("");
  const [kind, setKind] = useState("note");
  const textareaRef = useRef(null);

  const itemId = trip?.id;

  const fetchEvents = async () => {
    if (!itemId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/partner/shared-trips/${itemId}/timeline`);
      setEvents(data.events || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("You're no longer a participant on this trip");
        onClose && onClose();
      } else {
        toast.error("Failed to load timeline");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && itemId) fetchEvents();
    if (!open) { setDraft(""); setKind("note"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, itemId]);

  const post = async () => {
    const trimmed = (draft || "").trim();
    if (!trimmed) { toast.error("Write something first"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/partner/shared-trips/${itemId}/timeline`,
        { event_type: kind, content: trimmed },
      );
      if (data?.ok) {
        setEvents((prev) => [data.event, ...prev]);
        setDraft("");
      } else {
        toast.error("Post failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post failed");
    } finally { setSaving(false); }
  };

  const onDelete = async (ev) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await axios.delete(`${API}/partner/shared-trips/timeline/${ev.id}`);
      setEvents((prev) => prev.filter((e) => e.id !== ev.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const grouped = useMemo(() => {
    // Group by day for readability
    const byDay = new Map();
    for (const e of events) {
      const day = (e.created_at || "").slice(0, 10);
      const arr = byDay.get(day) || [];
      arr.push(e);
      byDay.set(day, arr);
    }
    return [...byDay.entries()];
  }, [events]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col" data-testid="trip-timeline-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="w-4 h-4 text-pink-500" /> {trip?.title || "Shared trip"}
          </DialogTitle>
          <DialogDescription>
            A private timeline shared with your partner. Only the two of you can see or post here.
          </DialogDescription>
        </DialogHeader>

        {/* Composer */}
        <div className="rounded-lg border border-pink-500/30 bg-pink-500/5 p-3">
          <div className="flex gap-2 mb-2 flex-wrap">
            {["note", "milestone", "reaction", "photo_ref"].map((k) => {
              const Icon = EVENT_ICON[k];
              return (
                <button
                  key={k} type="button"
                  onClick={() => setKind(k)}
                  className={`text-[11px] px-2 py-1 rounded-full border inline-flex items-center gap-1 ${
                    kind === k
                      ? "border-pink-500 bg-pink-500/20 text-pink-500"
                      : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`timeline-kind-${k}`}
                >
                  <Icon className="w-3 h-3" /> {k.replace("_", " ")}
                </button>
              );
            })}
          </div>
          <Textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Share a memory, mark a milestone, add a note…"
            rows={2}
            className="resize-none text-sm"
            data-testid="timeline-draft"
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); post(); }
            }}
          />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[11px] text-muted-foreground">⌘/Ctrl + Enter to post</span>
            <Button size="sm" onClick={post} disabled={saving || !draft.trim()} data-testid="timeline-post">
              <Send className="w-3.5 h-3.5 mr-1" /> {saving ? "Posting…" : "Post"}
            </Button>
          </div>
        </div>

        {/* Stream */}
        <div className="flex-1 overflow-y-auto space-y-3 pr-1 mt-2">
          {loading && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}
          {!loading && events.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">
              No posts yet — kick off the memory above.
            </p>
          )}
          {grouped.map(([day, list]) => (
            <div key={day}>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">{day}</div>
              <div className="space-y-2">
                {list.map((e) => {
                  const Icon = EVENT_ICON[e.event_type] || Heart;
                  const tint = EVENT_TINT[e.event_type] || "text-pink-500";
                  const mine = e.actor_id === currentUserId;
                  return (
                    <div
                      key={e.id}
                      className={`border rounded-lg p-2.5 ${mine ? "border-pink-500/30 bg-pink-500/5" : "border-border bg-card"}`}
                      data-testid={`timeline-event-${e.id}`}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <Icon className={`w-3.5 h-3.5 ${tint}`} />
                        <span className="text-sm font-medium">{e.actor_name}</span>
                        <Badge variant="outline" className="text-[10px]">{(e.event_type || "note").replace("_", " ")}</Badge>
                        <span className="text-[11px] text-muted-foreground ml-auto">{timeAgo(e.created_at)}</span>
                        {mine && (
                          <button
                            onClick={() => onDelete(e)}
                            className="text-[11px] text-muted-foreground hover:text-red-400"
                            aria-label="Delete"
                            data-testid={`timeline-delete-${e.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <p className="text-sm mt-1 whitespace-pre-wrap break-words">
                        {e.emoji ? <span className="mr-1">{e.emoji}</span> : null}
                        {e.content}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
